import { Loader } from "@googlemaps/js-api-loader";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import PropertyMapInfobox from "../PropertyOverview/PropertyMapInfobox";
import theme from "../../styles/styleTheme";

type PropertyMapProps = {
  propertyData: any;
  sx?: any;
};

const PropertyMap = ({ propertyData, sx }: PropertyMapProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [open, setOpen] = useState(false);
  const [currentProperty, setCurrentProperty] = useState({});
  const [markerClusterer, setMarkerClusterer] =
    useState<MarkerClusterer | null>(null);
  const [markerBounds, setMarkerBounds] =
    useState<google.maps.LatLngBounds | null>(null);
  const [loadedMap, setLoadedMap] = useState<boolean>(false);
  const [centerLat, setCenterLat] = useState<number | null>(null);
  const [centerLng, setCenterLng] = useState<number | null>(null);

  const key = process.env.REACT_APP_GOOGLE_API_KEY!;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const customMapStyle = [
    {
      featureType: "landscape",
      elementType: "geometry.fill",
      stylers: [{ color: "#fafafa" }],
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [{ color: "#d7dfff" }],
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "administrative",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road.arterial",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#d6d6d6" }],
    },
    {
      featureType: "road.local",
      elementType: "labels",
      stylers: [{ visibility: "on" }],
    },
    {
      featureType: "administrative.locality",
      elementType: "labels",
      stylers: [{ visibility: "on" }],
    },
    {
      featureType: "administrative.country",
      elementType: "geometry.stroke",
      stylers: [{ visibility: "on" }, { color: "#a3a3a3" }, { weight: 1 }],
    },
    {
      elementType: "labels.text.fill",
      stylers: [{ color: "#424242" }],
    },
    {
      elementType: "labels.text.stroke",
      stylers: [{ color: "#FFFFFF" }, { visibility: "off" }],
    },
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "water",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ];

  useEffect(() => {
    const initMap = async () => {
      const loader = new Loader({
        apiKey: key,
        version: "weekly",
      });

      await loader.load();

      const newMap = new google.maps.Map(
        document.getElementById("map") as HTMLElement,
        {
          center: { lat: 52.377956, lng: 4.89707 },
          zoom: 9,
          disableDefaultUI: true,
          styles: customMapStyle,
        }
      );

      setMap(newMap);
      setLoadedMap(true);
      setMarkerBounds(new google.maps.LatLngBounds());
    };

    if (!loadedMap) {
      initMap();
    }
  }, [loadedMap, key]);

  useEffect(() => {
    if (propertyData?.length > 0 && loadedMap) {
      updateMarkers();
    }
  }, [propertyData, loadedMap]);

  const updateMarkers = () => {
    if (!map || !markerBounds) return;

    let markers = [];
    for (let i = 0; i < propertyData?.length; i++) {
      if (propertyData[i].latitude === 0 || propertyData[i].longitude === 0) {
        continue;
      }
      var newMarker = markerGenerate(
        i,
        propertyData[i].latitude,
        propertyData[i].longitude
      );

      if (newMarker) {
        markers.push(newMarker);
      }
    }

    if (markerClusterer) {
      markerClusterer.clearMarkers();
    }

    const renderer = {
      render: ({ count, position }: any) => {
        return new google.maps.Marker({
          position,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: theme.palette.primary.main,
            fillOpacity: 0.7,
            strokeColor: "white",
            strokeWeight: 1,
            scale: Math.min(25, 6 + count * 0.3),
          },
          label: {
            text: String(count),
            color: "white",
            fontSize: "12px",
          },
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
      },
    };

    setMarkerClusterer(new MarkerClusterer({ markers, map, renderer }));

    map.fitBounds(markerBounds);

    google.maps.event.addListenerOnce(map, "bounds_changed", function () {
      if (propertyData?.length === 1) {
        map.setZoom(14);
      }
    });

    setCenterLat(map.getBounds()?.getCenter()?.lat() || null);
    setCenterLng(map.getBounds()?.getCenter()?.lng() || null);
  };

  const markerGenerate = (index: number, lat: number, lng: number) => {
    if (!lat || !lng || !map) return null;

    const position = { lat, lng };
    const marker = new google.maps.Marker({
      position,
      map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: "#5B41FF",
        fillOpacity: 0.9,
        strokeColor: "#5B41FF",
        strokeWeight: 1,
        scale: 6,
      },
    });

    if (markerBounds) {
      markerBounds.extend(position);
    }

    marker.addListener("click", () => {
      handleOpen();
      const current = propertyData?.filter(
        (property: any) =>
          property.propertyId === propertyData[index].propertyId
      );
      setCurrentProperty(current);
    });

    return marker;
  };

  return (
    <Box sx={{ width: "100%", borderRadius: 4, position: "relative", ...sx }}>
      <Box sx={{ width: "100%", height: "100%", position: "absolute" }}>
        <Box
          id="map"
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            ...sx,
          }}
        />
        <PropertyMapInfobox
          currentProperty={currentProperty}
          open={open}
          close={handleClose}
        />
      </Box>
    </Box>
  );
};

export default PropertyMap;
